import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Play, Clock, CheckCircle2, XCircle, Loader2, History, ChevronDown, ChevronUp } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  success: { bg: 'bg-slate-50 dark:bg-slate-900/30', text: 'text-slate-800 dark:text-slate-200', border: 'border-gray-200', icon: CheckCircle2 },
  error: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: XCircle },
  triggered: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: Clock },
};

const formatDate = (iso) => {
  if (!iso) return 'Jamais';
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatRelative = (iso) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "à l'instant";
  if (mins < 60) return `il y a ${mins}min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `il y a ${hours}h`;
  const days = Math.floor(hours / 24);
  return `il y a ${days}j`;
};

const TaskCard = ({ task, onExecute, executing, darkMode }) => {
  const [showHistory, setShowHistory] = useState(false);
  const lastExec = task.last_execution;
  const lastDate = lastExec?.executed_at || task.last_sent;
  const statusKey = lastExec?.status || 'triggered';
  const style = STATUS_STYLES[statusKey] || STATUS_STYLES.triggered;
  const StatusIcon = style.icon;

  return (
    <Card className={`overflow-hidden ${darkMode ? 'bg-gray-950 border-gray-800' : ''}`} data-testid={`task-card-${task.task_type}`}>
      <CardContent className="p-0">
        {/* Main row */}
        <div className="flex items-center gap-4 p-4">
          {/* Icon */}
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl shrink-0 ${task.enabled ? 'bg-slate-50 dark:bg-slate-900/30' : (darkMode ? 'bg-gray-900' : 'bg-gray-100')}`}>
            {task.icon}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{task.label}</h3>
              <Badge variant={task.enabled ? 'default' : 'secondary'} className="text-xs">
                {task.enabled ? 'Actif' : 'Inactif'}
              </Badge>
            </div>
            <div className={`flex items-center gap-3 mt-1 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <span>{task.frequency_label}</span>
              <span className={darkMode ? 'text-gray-600' : 'text-gray-300'}>|</span>
              <span>{task.time_of_day}</span>
            </div>
          </div>

          {/* Last execution */}
          <div className="text-right shrink-0">
            <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Dernière exécution</p>
            <p className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              {lastDate ? formatRelative(lastDate) : 'Jamais'}
            </p>
            {lastExec && (
              <div className="flex items-center gap-1 justify-end mt-0.5">
                <StatusIcon className={`w-3.5 h-3.5 ${style.text}`} />
                <span className={`text-xs ${style.text}`}>{statusKey === 'success' ? 'OK' : statusKey === 'error' ? 'Erreur' : 'En cours'}</span>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowHistory(p => !p)}
              data-testid={`history-btn-${task.task_type}`}
            >
              <History className="w-4 h-4 mr-1" />
              {showHistory ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </Button>
            <Button
              size="sm"
              onClick={() => onExecute(task.task_type)}
              disabled={executing === task.task_type}
              className="bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600"
              data-testid={`execute-btn-${task.task_type}`}
            >
              {executing === task.task_type
                ? <Loader2 className="w-4 h-4 animate-spin mr-1" />
                : <Play className="w-4 h-4 mr-1" />}
              Exécuter
            </Button>
          </div>
        </div>

        {/* History */}
        {showHistory && task.history && task.history.length > 0 && (
          <div className={`border-t ${darkMode ? 'border-gray-800 bg-black/50' : 'border-gray-100 bg-gray-50'}`}>
            <div className="px-4 py-2">
              <p className={`text-xs font-medium mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Historique récent
              </p>
              <div className="space-y-1.5">
                {task.history.map((h, i) => {
                  const hs = STATUS_STYLES[h.status] || STATUS_STYLES.triggered;
                  const HIcon = hs.icon;
                  return (
                    <div key={i} className={`flex items-center gap-3 text-xs py-1.5 px-2 rounded ${darkMode ? 'bg-gray-950' : 'bg-white'}`}>
                      <HIcon className={`w-3.5 h-3.5 shrink-0 ${hs.text}`} />
                      <span className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {formatDate(h.executed_at)}
                      </span>
                      <Badge variant="outline" className="text-[10px] py-0">
                        {h.trigger_type === 'manual' ? 'Manuel' : 'Auto'}
                      </Badge>
                      <span className={`truncate flex-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {h.message || '-'}
                      </span>
                      <span className={`shrink-0 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {h.triggered_by}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {showHistory && (!task.history || task.history.length === 0) && (
          <div className={`border-t px-4 py-4 text-center text-sm ${darkMode ? 'border-gray-800 text-gray-500' : 'border-gray-100 text-gray-400'}`}>
            Aucun historique d'exécution
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function HistoriqueTaches({ user }) {
  const { darkMode } = useTheme();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(null);

  const fetchTasks = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/task-history/tasks`);
      setTasks(res.data);
    } catch (err) {
      toast.error("Erreur lors du chargement des tâches");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const handleExecute = useCallback(async (taskType) => {
    setExecuting(taskType);
    try {
      const res = await axios.post(`${API}/task-history/execute/${taskType}`);
      if (res.data.status === 'success') {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message || 'Erreur lors de l\'exécution');
      }
      fetchTasks();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'exécution");
    } finally {
      setExecuting(null);
    }
  }, [fetchTasks]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-slate-700 dark:text-slate-300" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="historique-taches-page">
      {/* En-tête harmonisé */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Historique des Tâches</h1>
        </div>
      </div>

      <div className="space-y-3">
        {tasks.map(task => (
          <TaskCard
            key={task.task_type}
            task={task}
            onExecute={handleExecute}
            executing={executing}
            darkMode={darkMode}
          />
        ))}
      </div>
    </div>
  );
}
